import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ArrowBack,
  Save,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import { Post, PostStatus, PlatformDraft } from '../types/api';
import postsAPI from '../services/postsAPI';
import { formatTimeWithTimezone } from '../utils/timezone';
import PlatformContentTabs from '../components/Posts/PlatformContentTabs';
import { platformContentFromPost, platformDraftsToPayload } from '../utils/platformContent';

const EditPostPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [topic, setTopic] = useState('');
  const [status, setStatus] = useState<PostStatus>('DRAFT');
  const [scheduledTime, setScheduledTime] = useState<Dayjs | null>(null);
  const [platformDrafts, setPlatformDrafts] = useState<Record<string, PlatformDraft>>({});
  const [activePlatform, setActivePlatform] = useState('linkedin');

  useEffect(() => {
    if (id) {
      fetchPost();
    }
  }, [id]);

  const fetchPost = async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await postsAPI.getPost(id);
      const postData = response.data;
      
      setPost(postData);
      setTopic(postData.topic);
      setStatus(postData.status);

      const drafts = platformContentFromPost(postData.platform_content, postData.content);
      setPlatformDrafts(drafts);
      const keys = Object.keys(drafts);
      setActivePlatform(keys.includes('linkedin') ? 'linkedin' : keys[0] || 'linkedin');
      
      if (postData.scheduled_time) {
        setScheduledTime(dayjs(postData.scheduled_time));
      }
    } catch (err: any) {
      console.error('Failed to fetch post:', err);
      setError('Failed to load post. Please try again.');
      toast.error('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const handlePlatformContentChange = (platform: string, content: string) => {
    setPlatformDrafts((prev) => {
      const draft = prev[platform];
      if (!draft) return prev;
      return {
        ...prev,
        [platform]: {
          ...draft,
          content,
          char_count: content.length,
          within_limit: content.length <= draft.char_limit,
        },
      };
    });
  };

  const getLinkedInContent = () =>
    platformDrafts.linkedin?.content ?? post?.content ?? '';

  const handleSave = async () => {
    if (!id || !topic.trim() || !getLinkedInContent().trim()) {
      toast.error('Please fill in topic and content');
      return;
    }

    if (status === 'SCHEDULED' && (!scheduledTime || scheduledTime.isBefore(dayjs()))) {
      toast.error('Please select a future date and time for scheduling');
      return;
    }

    setSaving(true);
    
    try {
      const wasScheduled = post?.status === 'SCHEDULED';
      const isBeingScheduled = status === 'SCHEDULED' && !wasScheduled;
      const platforms = Object.keys(platformDrafts);

      const updateData = {
        topic: topic.trim(),
        content: getLinkedInContent().trim(),
        platforms: platforms.length > 0 ? platforms : post?.platforms,
        platform_content: platformDraftsToPayload(platformDrafts),
        status: isBeingScheduled ? ('DRAFT' as PostStatus) : status,
      };
      
      if (isBeingScheduled && scheduledTime) {
        await postsAPI.updatePost(id, updateData);
        await postsAPI.schedulePost(id, {
          scheduled_time: scheduledTime.toISOString(),
          platforms: platforms.length > 0 ? platforms : ['linkedin'],
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });
        toast.success(`Post updated and scheduled for ${scheduledTime.format('MMM DD, YYYY [at] h:mm A')}!`);
      } else if (status === 'SCHEDULED' && scheduledTime && wasScheduled) {
        await postsAPI.updatePost(id, updateData);
        await postsAPI.cancelSchedule(id);
        await postsAPI.schedulePost(id, {
          scheduled_time: scheduledTime.toISOString(),
          platforms: platforms.length > 0 ? platforms : ['linkedin'],
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });
        toast.success(`Post rescheduled for ${scheduledTime.format('MMM DD, YYYY [at] h:mm A')}!`);
      } else {
        await postsAPI.updatePost(id, updateData);
        toast.success('Post updated successfully!');
      }
      
      navigate('/posts');
    } catch (error: any) {
      console.error('Update error:', error);
      toast.error('Failed to update post. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: PostStatus): 'success' | 'warning' | 'info' | 'error' | 'default' => {
    switch (status) {
      case 'PUBLISHED':
        return 'success';
      case 'SCHEDULED':
        return 'warning';
      case 'DRAFT':
        return 'info';
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !post) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {error || 'Post not found'}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/posts')}
          sx={{ mt: 2 }}
        >
          Back to Posts
        </Button>
      </Container>
    );
  }

  const hasPlatformTabs = Object.keys(platformDrafts).length > 0;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate('/posts')}
                sx={{ mr: 2 }}
              >
                Back to Posts
              </Button>
              <Typography variant="h4" component="h1">
                Edit Post
              </Typography>
            </Box>
            
            <Chip
              label={post.status}
              color={getStatusColor(post.status)}
              size="small"
            />
          </Box>
          
          <Typography variant="body1" color="text.secondary">
            Update your post content and settings
          </Typography>
        </Box>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Post Topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Enter the topic or title for your post"
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              {hasPlatformTabs ? (
                <PlatformContentTabs
                  platformDrafts={platformDrafts}
                  activePlatform={activePlatform}
                  onActivePlatformChange={setActivePlatform}
                  onContentChange={handlePlatformContentChange}
                />
              ) : (
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  label="Post Content"
                  value={getLinkedInContent()}
                  onChange={(e) =>
                    handlePlatformContentChange('linkedin', e.target.value)
                  }
                  placeholder="Write your post content here..."
                  required
                />
              )}
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value as PostStatus)}
                >
                  <MenuItem value="DRAFT">Draft</MenuItem>
                  <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                  <MenuItem value="PUBLISHED">Published</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {status === 'SCHEDULED' && (
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Scheduled Date & Time"
                  value={scheduledTime}
                  onChange={(newValue) => setScheduledTime(newValue)}
                  minDateTime={dayjs()}
                  sx={{ width: '100%' }}
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Post Information
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Created:</strong> {formatTimeWithTimezone(post.created_at, 'Asia/Kolkata')}
              </Typography>
            </Grid>
            
            {post.published_time && (
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Published:</strong> {formatTimeWithTimezone(post.published_time, 'Asia/Kolkata')}
                </Typography>
              </Grid>
            )}
            
            {post.image_url && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Attached Image:
                </Typography>
                <Card variant="outlined" sx={{ maxWidth: 300 }}>
                  <img
                    src={post.image_url}
                    alt="Post content"
                    style={{
                      width: '100%',
                      height: '150px',
                      objectFit: 'cover'
                    }}
                  />
                  {post.image_credit && (
                    <CardContent sx={{ py: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {post.image_credit}
                      </Typography>
                    </CardContent>
                  )}
                </Card>
              </Grid>
            )}
          </Grid>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/posts')}
            disabled={saving}
          >
            Cancel
          </Button>
          
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
            onClick={handleSave}
            disabled={saving || !topic.trim() || !getLinkedInContent().trim()}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Container>
    </LocalizationProvider>
  );
};

export default EditPostPage;
